import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'

import { fetchSpecialRequestsRequestAction } from './actions'
import { getStatus, getIsFetching } from './selectors'
import { STATUS_NOT_OK, STATUS_OK } from './constants'

import { Typography } from '@s4l/ds'
import ButtonSimple from 'src/comps/ButtonSimple'
import CollapsibleSection from 'client/components/CollapsibleSection/CollapsibleSection'
import TextArea from 'src/comps/TextArea'
import RequestSentPreview from './RequestSentPreview'
import RequestSentMessage from './RequestSentMessage'
import Feedback from 'client/components/FeedbackVoucher'

import { IconCallCenter } from '@s4l/icons/IconCallCenter'
import { IconPlus } from '@s4l/icons/IconPlus'
import { IconMinus } from '@s4l/icons/IconMinus'

import { SpecialRequest } from './types'
import styles from './styles.module.css'

interface SpecialRequestsProps {
  specialRequests?: SpecialRequest[]
  hash: string
}

const SpecialRequests: React.FC<SpecialRequestsProps> = ({ specialRequests = [], hash }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const isLoading = useSelector(getIsFetching(hash))
  const status = useSelector(getStatus(hash))
  const [textAreaValue, setTextAreaValue] = useState<string>('')
  const [addedRequests, setAddedRequests] = useState<SpecialRequest[]>([])

  function handleAddNewSpecialRequests(): void {
    setAddedRequests(prev => [...prev, { requestValue: textAreaValue, datetime: new Date() }])
    dispatch(fetchSpecialRequestsRequestAction({ hash, hotelRequest: textAreaValue }))
    setTextAreaValue('')
  }

  return (
    <div className={styles.specialRequests}>
      <Typography className={styles.specialRequestsTitle as any} variant="title" size="s" as="h5">
        <FormattedMessage id="room.special-requests" />
      </Typography>

      <RequestSentMessage specialRequests={[...specialRequests, ...addedRequests]} />

      {specialRequests.length > 0 && (
        <RequestSentPreview
          hasBackground
          icon={IconCallCenter}
          title={intl.formatMessage({ id: 'room.special-requests-default-message' })}
        />
      )}

      <CollapsibleSection
        fullWidth
        data-testid="add-special-requests-section"
        title={intl.formatMessage({ id: 'special-requests' })}
        iconToOpen={IconPlus}
        iconToClose={IconMinus}
      >
        <div className={styles.addSpecialRequest}>
          <FormattedMessage id="checkout.special-requests-advice" tagName="span" />

          <TextArea
            role="textbox"
            placeholder={intl.formatMessage({ id: 'room.special-requests' })}
            value={textAreaValue}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setTextAreaValue(event.target.value)
            }
          />

          <ButtonSimple
            onClick={handleAddNewSpecialRequests}
            variant="primary"
            dataTestId="send-request-btn"
            text={intl.formatMessage({ id: 'global.send-request' })}
            isLoading={isLoading}
            disabled={textAreaValue === ''}
          />
        </div>
      </CollapsibleSection>

      {status === STATUS_OK && (
        <div className={styles.feedbackSection}>
          <Feedback
            dataTestId="status-ok"
            text={[intl.formatMessage({ id: 'room.special-requests-confirmation' })]}
            status={STATUS_OK}
          />
        </div>
      )}

      {status === STATUS_NOT_OK && (
        <div className={styles.feedbackSection}>
          <Feedback
            dataTestId="status-not-ok"
            text={[intl.formatMessage({ id: 'room.special-requests-notok' })]}
            status={STATUS_NOT_OK}
          />
        </div>
      )}
    </div>
  )
}

export default SpecialRequests
